
    // IMPORTS
    import { computed, defineComponent, provide, reactive, ref } from 'vue'
    import { AccountApi, buildLocalization, StringUtil, focusFirstInvalidField } from '@/util'

    import { Button } from '@progress/kendo-vue-buttons'
    import { DialogActionsBar } from '@progress/kendo-vue-dialogs'
    import { Input } from '@progress/kendo-vue-inputs'
    import { Error } from '@progress/kendo-vue-labels'
    import AppDialog from '@/components/common/app-dialog.vue'
    import PhoneField from '@/components/common/phone-field.vue'

    const MANDATORY_FIELDS = ['firstName', 'lastName', 'company', 'email', 'phoneNumber'] as const // phoneNumber validated by phone-field component via provide/inject
    type MandatoryField = typeof MANDATORY_FIELDS[number]
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    export default defineComponent({
        name: 'RequestAccountDialog',

        components: {
            AppDialog,
            PhoneField,
            'k-button': Button,
            'k-input': Input,
            DialogActionsBar,
            Error
        },

        emits: {
            close: null,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            done: (success: boolean) => true
        },

        setup(_, { emit }) {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            /********************************************************************************************************************
             STATE
             ********************************************************************************************************************/
            const form = reactive({
                firstName: '',
                lastName: '',
                company: '',
                email: '',
                phoneNumber: '',
                jobTitle: ''
            })

            const errors = reactive<Record<string, boolean>>({
                firstName: false,
                lastName: false,
                company: false,
                email: false
            })

            const emailInvalidFormat = ref(false)
            const isSubmitting = ref(false)
            const isValidationActive = ref(false)
            const phoneValid = ref(true)

            /********************************************************************************************************************
             PROVIDE
             ********************************************************************************************************************/
            provide('isValidationActive', isValidationActive)
            provide('setValidationResult', (id: string, isValid: boolean) => {
                if (id === 'phoneNumber') phoneValid.value = isValid
            })

            /********************************************************************************************************************
             COMPUTED
             ********************************************************************************************************************/
            const emailErrorMessage = computed(() =>
                emailInvalidFormat.value
                    ? localize('invalidEmail')
                    : localize('requiredField')
            )

            /********************************************************************************************************************
             HELPERS
             ********************************************************************************************************************/
            function validateField(field: MandatoryField): boolean {
                const value = form[field]
                const isEmpty = !StringUtil.hasContent(value)

                if (field === 'email') {
                    emailInvalidFormat.value = !isEmpty && !EMAIL_REGEX.test(value)
                    errors.email = isEmpty || emailInvalidFormat.value
                    return !errors.email
                }

                if (field === 'phoneNumber') {
                    return phoneValid.value
                }

                errors[field] = isEmpty
                return !isEmpty
            }

            function isFormValid(): boolean {
                isValidationActive.value = false
                isValidationActive.value = true
                // Validate all fields and collect results — avoid short-circuit so all errors are shown at once
                const results = MANDATORY_FIELDS.map(f => validateField(f))
                const isValid = results.every(r => r)

                if (!isValid) {
                    const failedResults = results
                    focusFirstInvalidField(
                        [...MANDATORY_FIELDS],
                        (id) => !failedResults[MANDATORY_FIELDS.indexOf(id as MandatoryField)]
                    )
                }

                return isValid
            }

            /********************************************************************************************************************
             EVENT HANDLERS
             ********************************************************************************************************************/
            function onInput(field: MandatoryField) {
                errors[field] = false
                if (field === 'email') emailInvalidFormat.value = false
            }

            function onSubmit() {
                if (!isFormValid()) {
                    return
                }

                isSubmitting.value = true

                const api = new AccountApi()
                api.requestAccount({
                    firstName: form.firstName,
                    lastName: form.lastName,
                    company: form.company,
                    email: form.email,
                    phoneNumber: form.phoneNumber,
                    jobTitle: StringUtil.hasContent(form.jobTitle) ? form.jobTitle : null
                })
                    .then(() => emit('done', true))
                    .catch(() => emit('done', false))
                    .finally(() => { isSubmitting.value = false })
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            const publicData = { form, errors, isSubmitting }
            const computedData = { emailErrorMessage }
            const publicMethods = { localize, onInput, onSubmit }

            return { ...publicData, ...computedData, ...publicMethods }
        }
    })
